/**
 * OKS-657 — deterministic close-focus for URL-state Sheets.
 *
 * Our URL-state Sheets (AddCandidateSheet, RewardSheet) render a Base UI
 * `Dialog.Popup` (via `ui/sheet.tsx`) with NO captured trigger — they open
 * from a URL param (`?add=1` / `?new=1`), not from a `SheetTrigger`. Base UI's
 * default return-focus therefore has no target and focus falls to `<body>` on
 * close, so a keyboard user's next Tab restarts from the top of the document
 * (evidence: UX-Spec-OKS-629 §9, RESOLVED).
 *
 * This helper pins close-focus to a deterministic, always-mounted list-header
 * control, identified by its `data-testid`. It returns a getter suitable for
 * the Base UI `Dialog.Popup` `finalFocus` prop (which `SheetContent` spreads
 * onto the popup — so callers wire it via call-site props rather than a
 * ledgered edit to `ui/sheet.tsx` (permitted with parity/a11y coverage; see
 * `docs/frontend/README.md` rule 6 — call-site props are cheaper here).
 *
 * Why a DOM getter resolved at CLOSE time rather than a threaded React ref:
 *  - The candidate "Add" trigger (`add-candidate-trigger`) renders in TWO
 *    places (list header + empty-state CTA); a single ref can't cover both.
 *    `querySelector` resolves the first matching mounted control.
 *  - The RewardSheet opens from URL state and from per-row edit buttons that
 *    re-render after a mutation; a ref captured at open can go stale. Resolving
 *    at close time always finds the live header control.
 *
 * Null-path (header control transiently unmounted): Base UI treats a `null`
 * return as "fall back to the default behavior" — for our trigger-less Sheets
 * that means focus is released (not trapped), no crash, no console error. On
 * the normal path both header controls are always mounted, so the getter
 * returns the element and focus lands there. The getter deliberately never
 * returns `false`/`undefined`, which Base UI reads as "do nothing" (focus could
 * stay inside the unmounting Sheet).
 *
 * The resolved element MUST be a focusable node carrying the testid (the actual
 * `<button>`, not a wrapper) so both Base UI focus-move and the Playwright
 * `toBeFocused()` canaries resolve against the same node.
 */

/**
 * Build a Base UI `finalFocus` getter that focuses the first mounted element
 * carrying `data-testid={testId}` at the moment the Sheet closes.
 *
 * @param testId - the `data-testid` of the focusable header control to pin
 *   close-focus to (e.g. `"add-candidate-trigger"`, `"rewards-new-cta"`).
 * @returns a getter returning the element to focus, or `null` to let Base UI
 *   fall back to its default (released, not trapped).
 */
export function finalFocusByTestId(testId: string): () => HTMLElement | null {
  return () => document.querySelector<HTMLElement>(`[data-testid="${testId}"]`)
}
